import React, {FC, useEffect, useState} from 'react';

import {PLACE_OF_INTERACTION} from '@optimaxdev/analytics/desktop/constants/placeOfInteraction';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {PlacesOfInteractionType} from '@optimaxdev/analytics/desktop/types/placeOfInteraction';
import {MediaQuery, QuantityButton} from '@optimaxdev/design-system';
import {connect} from 'react-redux';
import {useMediaQuery} from 'react-responsive';

import {CART_BUTTON} from 'constants/analytics/events/cart';
import {ApplicationStoreType} from 'desktop/store';
import {sessionStore} from 'libs/storage/storage';
import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';

import {addAccessory} from '../../store/reducers/accessories/accessories';
import {
    changeItemQtyAction,
    ItemChangeQtyType,
} from '../../store/reducers/changeItemQty/changeItemQty';
import {setPlaceOfInteraction} from '../../store/reducers/placeOfInteraction/placeOfInteraction';
import {removeQuoteItem} from '../../store/reducers/quoteItems/quoteItems';
import {
    getAccessoryLoadingId,
    getCartItemByProductId,
    isAccessoriesDisabled,
} from '../../store/selectors';
import {CartItemAccessoryType, CartItemType} from '../../types';

export const ACCESSORY_QUANTITY_MORE_THAN_TWO = 'accessoryQuantityMoreThanTwo';

export type PropsType = {
    accessoryId: string;
    addToCart: (productId: string) => void;
    changeQty: (accessoryData: ItemChangeQtyType) => void;
    addedAccessory: CartItemAccessoryType;
    isUpdatingCartItems: boolean;
    removeAccessory: (data: {item: CartItemType; isNotice?: boolean}) => void;
    accessoryLoadingId: number;
    savePlaceOfInteraction: (interaction: PlacesOfInteractionType) => void;
    className?: string;
};

/**
 * returns the width of the button by checking the extensions and flag
 */
const getQuantityButtonWidth = (sm: boolean, md: boolean) => {
    if (sm) return 174;
    else if (md) return 210;
    return 240;
};

const sendAnalytics = () => {
    spawnPriorityTask(() => {
        if (!sessionStore.get(ACCESSORY_QUANTITY_MORE_THAN_TWO)) {
            sendDesktopAnalyticsEvent(CART_BUTTON, {
                action: 'Accessories Section',
                label: 'Add Item - Quantity Max Limit',
            });
            sessionStore.set(ACCESSORY_QUANTITY_MORE_THAN_TWO, true);
        }
    });
};

/**
 * Cart accessory offer item
 * @constructor
 */
export const AccessoryQuantityButton: FC<PropsType> = ({
    accessoryId,
    accessoryLoadingId,
    addedAccessory = {} as CartItemAccessoryType,
    addToCart,
    changeQty,
    className,
    isUpdatingCartItems,
    removeAccessory,
    savePlaceOfInteraction,
}) => {
    const [isMaxQty, setIsMaxQty] = useState(false);
    const {id, product_id: productId, qty = 0} = addedAccessory;
    const mediaSm = useMediaQuery(MediaQuery.sm);
    const mediaMd = useMediaQuery(MediaQuery.md);

    useEffect(() => {
        if (isMaxQty && qty === 1) setIsMaxQty(false);
    }, [qty]);

    /**
     * Change count of added to cart accessory
     * @param {number} value  Current value
     */
    const changeAddedAccessoryQty = (value: number) => {
        if (!id) return;
        const accessoryData = {productId, qty: value, quoteItemId: id};
        if (value > Number(qty)) {
            changeQty({...accessoryData, notificationType: 'Added'});
        } else {
            changeQty({...accessoryData, notificationType: 'Removed'});
        }
        savePlaceOfInteraction(PLACE_OF_INTERACTION.ACCESSORIES_QUANTITY_CTA);
    };

    /**
     * Change count of current accessory
     * @param {number} value  Current value
     */
    const onChangeQty = (value: number) => {
        if (!qty) {
            addToCart(accessoryId);
            savePlaceOfInteraction(PLACE_OF_INTERACTION.ACCESSORIES_ADD_CTA);
        } else if (value <= 2) {
            if (value === 0 && id) {
                removeAccessory({isNotice: true, item: addedAccessory as CartItemAccessoryType});
            } else {
                changeAddedAccessoryQty(value);
            }
        } else {
            setIsMaxQty(true);
            sendAnalytics();
        }
    };

    return (
        <div className={className}>
            <QuantityButton
                isDisabled={isUpdatingCartItems}
                isLoading={Number(accessoryId) === accessoryLoadingId}
                isMax={isMaxQty}
                onChange={onChangeQty}
                size="medium"
                value={qty as number}
                width={getQuantityButtonWidth(mediaSm, mediaMd)}
            >
                Add to Cart
            </QuantityButton>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @param {PropsType} props - props
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType, {accessoryId}: {accessoryId: string}) => ({
    accessoryLoadingId: getAccessoryLoadingId(store),
    addedAccessory: getCartItemByProductId(store, accessoryId),
    isUpdatingCartItems: isAccessoriesDisabled(store),
});

export const AccessoryQuantityButtonConnected = connect(mapStateToProps, {
    addToCart: addAccessory,
    changeQty: changeItemQtyAction,
    removeAccessory: removeQuoteItem,
    savePlaceOfInteraction: setPlaceOfInteraction,
})(AccessoryQuantityButton);
